import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import {
  DigitalSection,
  FaerbungReviewCategories,
} from '../../../redux/types';
import { selectAllDigitalCases } from '../../../redux/reducers/SurveyDataReducer';
import {
  addUpdateFaerbungReviewMetas,
  fetchFaerbungReviewMetas,
  resetFaerbungReviewMetas,
} from '../../../redux/actions/faerbungReviewAction';
import {
  selectFaerbungReviewMeta,
  selectSelectedFaerbungReviewMeta,
} from '../../../redux/reducers/FaerbungReviewReducer';
import useCreateDeleteReviewMeta from '../../../hooks/faerbungReview/useCreateDeleteReviewMeta';
import { realNumberRegex } from '../../../utilities/regex';
import useDebounce from '../../../hooks/useDebounce';
import useUpdateMaxFaerbungScores from '../../../hooks/faerbungReview/useUpdateMaxFaerbungScores';

type Props = {
  sections: DigitalSection[];
  selectedSection: DigitalSection;
  surveyId: number;
}

type TextBuffer = {
  text: string,
  error: boolean,
  saved: boolean,
  category?: FaerbungReviewCategories
}

export const faerbungCategoryTextMap = {
  [FaerbungReviewCategories.sensitivity]: 'faerbung.sensitivity',
  [FaerbungReviewCategories.specificity]: 'faerbung.specifity',
};

const FaerbungReviewSettings: React.FC<Props> = ({
  surveyId,
  selectedSection,
  sections,
}: Props) => {
  const dispatch = useAppDispatch();
  const allCases = useAppSelector((state) => selectAllDigitalCases(state));
  const faerbungReviewMetas = useAppSelector(selectFaerbungReviewMeta);
  const selectedFaerbungReviewMeta = useAppSelector(
    (state) => selectSelectedFaerbungReviewMeta(state, selectedSection.id),
  );
  const [bufferPassScore, setBufferPassScore] = useState<TextBuffer>();
  const [bufferCatScore, setBufferCatScore] = useState<Required<TextBuffer>[]>();

  /** Auto-Update Hooks */
  useCreateDeleteReviewMeta(sections, surveyId, allCases, faerbungReviewMetas);
  useUpdateMaxFaerbungScores(allCases, selectedSection, selectedFaerbungReviewMeta);

  useEffect(() => {
    dispatch(fetchFaerbungReviewMetas(surveyId));
    return () => {
      dispatch(resetFaerbungReviewMetas());
    };
  }, [dispatch, surveyId]);

  useEffect(() => {
    /** set score values in input buffers  */
    if (selectedFaerbungReviewMeta) {
      setBufferPassScore({
        text: selectedFaerbungReviewMeta.passingScore.toString(),
        error: false,
        saved: true,
      });
      const catBuffer: Required<TextBuffer>[] = selectedFaerbungReviewMeta.categories.map((c) => ({
        text: c.score.toString(),
        error: false,
        saved: true,
        category: c.category,
      }));
      setBufferCatScore(catBuffer);
    }
  }, [selectedFaerbungReviewMeta]);

  const onPassScoreChange = (val: string) => {
    if (!selectedFaerbungReviewMeta) {
      return;
    }
    const test = realNumberRegex.test(val);
    if (test) {
      const { maxScore } = selectedFaerbungReviewMeta;
      if ((Number(val) > maxScore)) {
        setBufferPassScore({
          text: val,
          error: true,
          saved: false,
        });
        return;
      }
      dispatch(addUpdateFaerbungReviewMetas([{
        ...selectedFaerbungReviewMeta,
        passingScore: Number(val),
      }]));
      setBufferPassScore({
        text: val,
        error: false,
        saved: true,
      });
    } else {
      setBufferPassScore({
        text: '',
        error: true,
        saved: false,
      });
    }
  };

  const onCategoryScoreChange = (val: Required<TextBuffer>[]): void => {
    if (!selectedFaerbungReviewMeta) {
      return;
    }
    let error = false;
    const erroredBuffer = val.map((v) => {
      const test = realNumberRegex.test(v.text);
      if (!test) {
        error = true;
        return { ...v, error: true, saved: false };
      }
      return v;
    });
    if (error) {
      setBufferCatScore(erroredBuffer);
      return;
    }
    dispatch(addUpdateFaerbungReviewMetas([{
      ...selectedFaerbungReviewMeta,
      categories: val.map((v) => ({
        category: v.category,
        score: Number(v.text),
      })),
    }]));
    setBufferCatScore(val.map((v) => ({ ...v, saved: true, error: false })));
  };

  const passingScoreDebouncer = useDebounce<string>(onPassScoreChange, 500);
  const categoryScoreDebouncer = useDebounce<Required<TextBuffer>[]>(onCategoryScoreChange, 500);

  const onPassScoreChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const score = e.target.value;
    setBufferPassScore({
      text: score,
      error: false,
      saved: false,
    });
    passingScoreDebouncer(score);
  };

  const onCategoryScoreChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>, c: FaerbungReviewCategories,
  ) => {
    const score = e.target.value;
    if (!bufferCatScore) {
      return;
    }
    const updatedCatState = bufferCatScore.map((o) => {
      if (o.category === c) {
        return {
          ...o,
          text: score,
          error: false,
          saved: false,
        };
      }
      return o;
    });
    setBufferCatScore(updatedCatState);
    categoryScoreDebouncer(updatedCatState);
  };

  if (!selectedFaerbungReviewMeta) {
    return null;
  }
  return (
    <Box>
      <Box sx={{ my: 1 }} display="flex" alignItems="center">
        <Typography sx={{ flexBasis: 230 }} fontWeight={500}>
          Faerbung section scores
        </Typography>
        <Box sx={{ gap: 1 }} display="flex" alignItems="center">
          <TextField
            value={bufferPassScore?.text || ''}
            label="Pass score"
            onChange={onPassScoreChangeHandler}
            error={bufferPassScore?.error}
          />
          <Tooltip title="Possible maximum score depends on the category scores times the number of cases">
            <TextField
              value={selectedFaerbungReviewMeta.maxScore}
              label="Possible Max Score"
              disabled
            />
          </Tooltip>
        </Box>
        {
          bufferPassScore?.saved && (
            <Typography sx={{ mx: 2 }} variant="caption"><FormattedMessage id="submission.saved" /></Typography>
          )
        }
      </Box>
      <Box sx={{ my: 4 }}>
        <Typography variant="h6">
          Faerbung Review Category scores
        </Typography>
        {
          bufferCatScore && bufferCatScore.map((m) => (
            <Box key={m.category}>
              {
                faerbungCategoryTextMap[m.category] && (
                  <Box sx={{ my: 2 }} display="flex" alignItems="center">
                    <Typography sx={{ flexBasis: 230 }}>
                      <FormattedMessage id={faerbungCategoryTextMap[m.category]} />
                    </Typography>
                    <Box sx={{ gap: 1 }} display="flex" alignItems="center">
                      <TextField
                        value={m.text}
                        onChange={
                          (
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => onCategoryScoreChangeHandler(e, m.category)
                        }
                        error={m.error}
                      />
                      {
                        m.saved && (
                          <Typography sx={{ mx: 2 }} variant="caption"><FormattedMessage id="submission.saved" /></Typography>
                        )
                      }
                    </Box>
                  </Box>
                )
              }
            </Box>
          ))
        }
      </Box>
    </Box>
  );
};

export default FaerbungReviewSettings;
